import React, { useState } from 'react';
import {
  ArrowDownToLine,
  Check,
  FileText,
  Mail,
  Send,
  ShieldCheck,
} from 'lucide-react';
import { EstadiaAccess } from './access';
import { ColumnHint, DataHint } from './data-hint';
import { Modal, TripTable } from './components';
import { MetricCard } from './indicators';
import type { EstadiaCommand, Justification } from './live-data';
import {
  DemoState,
  DEMO_DAY,
  Invoice,
  INVOICE_LABELS,
  RECIPIENTS,
  Trip,
  canBill,
  createInvoice,
  dateTime,
  decideInvoice,
  deliverInvoice,
  duration,
  exportCsv,
  invoiceTotal,
  metrics,
  money,
  resubmitInvoice,
  sum,
  validateTrip,
  hasTripData,
} from './model';

type Update = (
  action: (state: DemoState) => DemoState,
  message: string
) => void;
type Stage =
  'pending' | 'ready' | 'billed' | 'rejected' | 'active' | 'none' | 'free';
const STAGES: Record<Stage, string> = {
  pending: 'Conferência pendente',
  ready: 'Disponível para faturar',
  billed: 'Faturada',
  rejected: 'Rejeitada na operação',
  active: 'Em apuração',
  none: 'Sem direito',
  free: 'Dentro da franquia',
};
function stageOf(state: DemoState, trip: Trip): Stage {
  const value = metrics(trip);
  if (!value.eligible) return 'none';
  if (!value.cents) return 'free';
  if (!value.complete) return 'active';
  if (trip.validation === 'rejected') return 'rejected';
  if (
    state.invoices.some(
      (invoice) =>
        invoice.status !== 'reversed' && invoice.tripIds.includes(trip.id)
    )
  )
    return 'billed';
  return canBill(state, trip) ? 'ready' : 'pending';
}
function exportInvoice(state: DemoState, invoice: Invoice) {
  exportCsv(
    `${invoice.id}.csv`,
    [
      [
        'Fatura',
        'Transportadora',
        'Aprovação',
        'Viagem',
        'Placa',
        'Local',
        'Data agendamento',
        'REC',
        'EXP',
        'Total',
        'Horas adicionais',
        'Tarifa R$/hora',
        'Valor',
        'CNPJ NF',
        'Logradouro NF',
      ],
      ...state.trips
        .filter((trip) => invoice.tripIds.includes(trip.id))
        .map((trip) => {
          const value = metrics(trip);
          return [
            invoice.id,
            invoice.carrier,
            INVOICE_LABELS[invoice.status],
            trip.sourceId || trip.id,
            trip.plate,
            trip.unit,
            dateTime(trip.events.scheduled),
            duration(value.rec),
            duration(value.exp),
            duration(value.total),
            duration(value.excess),
            '46,62',
            money(value.cents),
            trip.cnpj,
            trip.address,
          ];
        }),
    ],
    state.trips.filter((trip) => invoice.tripIds.includes(trip.id))
  );
}

export default function Settlement({
  state,
  rows,
  access,
  update,
  onSelect,
  referenceDate = DEMO_DAY,
  onCommand,
  justifications,
  busy,
  section = 'all',
}: {
  state: DemoState;
  rows: Trip[];
  access: EstadiaAccess;
  update: Update;
  onSelect: (trip: Trip) => void;
  referenceDate?: string;
  onCommand?: (command: EstadiaCommand) => Promise<void>;
  justifications?: { trip: Justification[]; billing: Justification[] };
  busy?: boolean;
  section?: 'all' | 'stays' | 'invoices';
}) {
  const nextCycle = new Date(`${referenceDate}T12:00:00`);
  nextCycle.setDate(nextCycle.getDate() + ((4 - nextCycle.getDay() + 7) % 7));
  const [status, setStatus] = useState('all');
  const [onlyPayable, setOnlyPayable] = useState(true);
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [invoiceStatus, setInvoiceStatus] = useState('all');
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [rejectTrip, setRejectTrip] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [justificationId, setJustificationId] = useState('');
  const [actionError, setActionError] = useState('');
  async function run(
    command: EstadiaCommand,
    fallback: (current: DemoState) => DemoState,
    message: string
  ) {
    setActionError('');
    try {
      if (onCommand) await onCommand(command);
      else update(fallback, message);
      return true;
    } catch (error) {
      setActionError(
        error instanceof Error
          ? error.message
          : 'Não foi possível concluir a ação.'
      );
      return false;
    }
  }
  const visible = rows.filter(
    (trip) =>
      (!onlyPayable || metrics(trip).cents > 0) &&
      (status === 'all' || stageOf(state, trip) === status)
  );
  const eligible = visible.filter((trip) => canBill(state, trip));
  const selectedTrips = eligible.filter((trip) => selected.includes(trip.id));
  const selectedIds = selectedTrips.map((trip) => trip.id);
  const selectedCarrier = selectedTrips[0]?.carrier;
  const selectable = eligible.filter(
    (trip) => !selectedCarrier || trip.carrier === selectedCarrier
  );
  const allowSelectAll = Boolean(
    selectable.length &&
    (selectedCarrier ||
      new Set(selectable.map((trip) => trip.carrier)).size === 1)
  );
  const rowIds = rows.map((trip) => trip.id);
  const invoices = state.invoices.filter((invoice) =>
    invoice.tripIds.some((id) => rowIds.includes(id))
  );
  const visibleInvoices = invoices.filter(
    (invoice) =>
      invoiceStatus === 'all' ||
      (invoiceStatus === 'dispatch'
        ? invoice.status === 'approved' &&
          !state.deliveries.some(
            (delivery) => delivery.invoiceId === invoice.id
          )
        : invoice.status === invoiceStatus)
  );
  const target = state.invoices.find((invoice) => invoice.id === invoiceId);
  const delivery = state.deliveries.find(
    (item) => item.invoiceId === invoiceId
  );
  const maxPage = Math.max(0, Math.ceil(visible.length / 15) - 1);
  const currentPage = Math.min(page, maxPage);
  const pending = rows.filter((trip) => stageOf(state, trip) === 'pending');
  const ready = rows.filter((trip) => canBill(state, trip));
  const approvalQueue = invoices.filter(
    (invoice) => invoice.status === 'pending'
  );
  const dispatchQueue = invoices.filter(
    (invoice) =>
      invoice.status === 'approved' &&
      !state.deliveries.some((item) => item.invoiceId === invoice.id)
  );
  function openInvoice(id: string) {
    setInvoiceId(id);
    setReason('');
    setJustificationId('');
    setActionError('');
  }
  function filterInvoices(status: string) {
    setInvoiceStatus(status);
    document
      .getElementById('stay-invoices')
      ?.scrollIntoView?.({ block: 'start' });
  }
  return (
    <>
      {busy && <p role="status">Salvando no servidor…</p>}
      {actionError && (
        <div className="stay-callout error" role="alert">
          {actionError}
        </div>
      )}
      <div className={`stay-stat-grid ${access.viewInvoices ? '' : 'two'}`}>
        {section !== 'invoices' && (
          <>
            <MetricCard
              title="Conferência operacional"
              value={pending.length}
              detail="Viagens encerradas aguardando validação"
              tone="amber"
              onClick={() => {
                setStatus('pending');
                setPage(0);
              }}
            />
            <MetricCard
              title="Disponível para faturar"
              value={money(sum(ready.map((trip) => metrics(trip).cents)))}
              detail={`${ready.length} viagens validadas, ainda não faturadas`}
              tone="green"
              onClick={() => {
                setStatus('ready');
                setPage(0);
              }}
            />
          </>
        )}
        {section !== 'stays' && access.viewInvoices && (
          <>
            <MetricCard
              title="Aprovação Corteva"
              value={money(
                sum(
                  approvalQueue.map((invoice) => invoiceTotal(state, invoice))
                )
              )}
              detail={`${approvalQueue.length} faturas aguardando decisão`}
              tone="amber"
              onClick={() => filterInvoices('pending')}
            />
            <MetricCard
              title="A encaminhar ao financeiro"
              value={money(
                sum(
                  dispatchQueue.map((invoice) => invoiceTotal(state, invoice))
                )
              )}
              detail={`${dispatchQueue.length} faturas aprovadas`}
              onClick={() => filterInvoices('dispatch')}
            />
          </>
        )}
      </div>
      {section !== 'invoices' && (
        <section className="stay-card">
          <div className="stay-card-heading">
            <div>
              <h2>Estadias</h2>
              <p>Conferência das viagens e seleção para faturamento.</p>
            </div>
            <span>{visible.length} viagens</span>
          </div>
          <div className="stay-toolbar">
            <label>
              <input
                type="checkbox"
                checked={onlyPayable}
                onChange={(event) => {
                  setOnlyPayable(event.target.checked);
                  setPage(0);
                }}
              />{' '}
              Somente com valor a pagar
            </label>
            <select
              aria-label="Etapa da estadia"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(0);
              }}
            >
              <option value="all">Todas as etapas</option>
              {Object.entries(STAGES).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="stay-table-wrap">
            <table>
              <thead>
                <tr>
                  {access.createInvoice && (
                    <th>
                      <DataHint
                        title="Seleção de viagens"
                        description="Selecione viagens validadas da mesma transportadora. Após escolher a primeira viagem, a seleção em lote considera somente essa transportadora."
                      >
                        <input
                          aria-label="Selecionar viagens da transportadora"
                          type="checkbox"
                          disabled={!allowSelectAll}
                          checked={Boolean(
                            allowSelectAll &&
                            selectable.every((trip) =>
                              selectedIds.includes(trip.id)
                            )
                          )}
                          onChange={(event) =>
                            setSelected(
                              event.target.checked
                                ? selectable.map((trip) => trip.id)
                                : []
                            )
                          }
                        />
                      </DataHint>
                    </th>
                  )}
                  <th>Veículo / viagem</th>
                  <th>
                    {rows.some((trip) => hasTripData(trip, 'Unidade'))
                      ? 'Local / agendamento'
                      : 'Agendamento'}
                  </th>
                  <th>Transportadora</th>
                  <th>
                    <ColumnHint
                      label="Permanência"
                      description="Total de tempo parado apurado pela soma REC + EXP."
                    />
                  </th>
                  <th>
                    <ColumnHint
                      label="Horas adicionais"
                      description="Tempo elegível acima da franquia de 10 horas."
                    />
                  </th>
                  <th>Valor</th>
                  <th>Etapa</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {visible
                  .slice(currentPage * 15, currentPage * 15 + 15)
                  .map((trip) => {
                    const value = metrics(trip);
                    const stage = stageOf(state, trip);
                    const invoice = state.invoices.find((item) =>
                      item.tripIds.includes(trip.id)
                    );
                    return (
                      <tr key={trip.id}>
                        {access.createInvoice && (
                          <td>
                            <input
                              aria-label={`Selecionar ${trip.id}`}
                              type="checkbox"
                              disabled={
                                !canBill(state, trip) ||
                                Boolean(
                                  selectedCarrier &&
                                  trip.carrier !== selectedCarrier
                                )
                              }
                              checked={selectedIds.includes(trip.id)}
                              onChange={(event) =>
                                setSelected(
                                  event.target.checked
                                    ? [...selectedIds, trip.id]
                                    : selectedIds.filter((id) => id !== trip.id)
                                )
                              }
                            />
                          </td>
                        )}
                        <td>
                          <button
                            className="stay-link"
                            onClick={() => onSelect(trip)}
                          >
                            <b>{trip.plate}</b>
                            <small>{trip.sourceId || trip.id}</small>
                          </button>
                        </td>
                        <td>
                          {hasTripData(trip, 'Unidade') && trip.unit}
                          <small>{dateTime(trip.events.scheduled)}</small>
                        </td>
                        <td>{trip.carrier}</td>
                        <td>{duration(value.total)}</td>
                        <td>{duration(value.excess)}</td>
                        <td>{money(value.cents)}</td>
                        <td>
                          <span
                            className={`stay-pill ${stage === 'pending' || stage === 'active' ? 'pending' : stage === 'rejected' ? 'rejected' : stage === 'none' ? 'none' : 'ok'}`}
                          >
                            {STAGES[stage]}
                          </span>
                          {trip.validationReason && stage === 'rejected' && (
                            <small>{trip.validationReason}</small>
                          )}
                        </td>
                        <td>
                          <div className="stay-actions">
                            {(stage === 'pending' || stage === 'rejected') &&
                              access.validate && (
                                <button
                                  className="stay-primary"
                                  disabled={busy}
                                  onClick={() =>
                                    run(
                                      { type: 'validate', id: trip.id },
                                      (current) =>
                                        validateTrip(current, trip.id, true),
                                      'Viagem validada e disponível para faturamento.'
                                    )
                                  }
                                >
                                  <Check size={14} />{' '}
                                  {stage === 'rejected'
                                    ? 'Revalidar'
                                    : 'Validar'}
                                </button>
                              )}
                            {stage === 'pending' && access.rejectValidation && (
                              <button
                                onClick={() => {
                                  setRejectTrip(trip.id);
                                  setReason('');
                                  setJustificationId('');
                                }}
                              >
                                Rejeitar
                              </button>
                            )}
                            {invoice && access.viewInvoices && (
                              <button
                                className="stay-link"
                                onClick={() => openInvoice(invoice.id)}
                              >
                                {invoice.id}
                              </button>
                            )}
                            {stage !== 'pending' && !invoice && (
                              <button
                                className="stay-link"
                                onClick={() => onSelect(trip)}
                              >
                                Detalhes
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          {!visible.length && (
            <div className="stay-empty-state">
              Nenhuma viagem corresponde aos filtros selecionados.
            </div>
          )}
          <div className="stay-pagination">
            <span>
              {visible.length} viagens · página {currentPage + 1} de{' '}
              {maxPage + 1}
            </span>
            <button
              disabled={!currentPage}
              onClick={() => setPage(currentPage - 1)}
            >
              Anterior
            </button>
            <button
              disabled={currentPage === maxPage}
              onClick={() => setPage(currentPage + 1)}
            >
              Próxima
            </button>
          </div>
          {access.createInvoice && (
            <div className="stay-billing-summary">
              <span>
                <b>
                  {selectedIds.length} viagens selecionadas ·{' '}
                  {money(sum(selectedTrips.map((trip) => metrics(trip).cents)))}
                </b>
                <small>
                  {selectedCarrier ||
                    'Selecione viagens da mesma transportadora para consolidar a fatura.'}
                </small>
              </span>
              <button
                className="stay-primary"
                disabled={!selectedIds.length || busy}
                onClick={async () => {
                  const success = await run(
                    { type: 'createInvoice', ids: selectedIds },
                    (current) => createInvoice(current, selectedIds),
                    'Fatura gerada e submetida à aprovação Corteva.'
                  );
                  if (success) setSelected([]);
                }}
              >
                <Send size={16} /> Gerar e submeter fatura
              </button>
            </div>
          )}
        </section>
      )}
      {section !== 'stays' && access.viewInvoices && (
        <section className="stay-card" id="stay-invoices">
          <div className="stay-card-heading">
            <div>
              <h2>Faturas e aprovações</h2>
              <p>
                Ciclo de aprovação: quinta-feira ·{' '}
                {nextCycle.toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                })}
              </p>
              <p>
                Aprovação, histórico e encaminhamento financeiro na mesma
                fatura.
              </p>
            </div>
            <select
              aria-label="Status das faturas"
              value={invoiceStatus}
              onChange={(event) => setInvoiceStatus(event.target.value)}
            >
              <option value="all">Todos os status</option>
              {Object.entries(INVOICE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
              <option value="dispatch">A encaminhar ao financeiro</option>
            </select>
          </div>
          <div className="stay-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Fatura / emissão</th>
                  <th>Transportadora</th>
                  <th>Viagens</th>
                  <th>Valor consolidado</th>
                  <th>Aprovação</th>
                  <th>Financeiro</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {[...visibleInvoices].reverse().map((invoice) => {
                  const record = state.deliveries.find(
                    (item) => item.invoiceId === invoice.id
                  );
                  return (
                    <tr key={invoice.id}>
                      <td>
                        {invoice.id}
                        <small>{dateTime(invoice.createdAt)}</small>
                      </td>
                      <td>{invoice.carrier}</td>
                      <td>{invoice.tripIds.length}</td>
                      <td>{money(invoiceTotal(state, invoice))}</td>
                      <td>
                        <span className={`stay-pill ${invoice.status}`}>
                          {INVOICE_LABELS[invoice.status]}
                        </span>
                      </td>
                      <td>
                        {record ? (
                          <>
                            <span className="stay-pill approved">
                              {onCommand
                                ? record.status
                                : 'Encaminhamento registrado'}
                            </span>
                            <small>{dateTime(record.at)}</small>
                          </>
                        ) : invoice.status === 'approved' ? (
                          'A encaminhar'
                        ) : (
                          'Aguardando aprovação'
                        )}
                      </td>
                      <td>
                        <button
                          className="stay-link"
                          onClick={() => openInvoice(invoice.id)}
                        >
                          Abrir fatura
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!visibleInvoices.length && (
            <div className="stay-empty-state">
              Nenhuma fatura corresponde aos filtros selecionados.
            </div>
          )}
        </section>
      )}
      {rejectTrip && (
        <Modal
          title={`Rejeitar ${rejectTrip}`}
          description="Registre o motivo da divergência operacional."
          onClose={() => setRejectTrip(null)}
        >
          <label className="stay-field">
            Motivo obrigatório
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          {onCommand && (
            <label className="stay-field">
              Justificativa
              <select
                value={justificationId}
                onChange={(event) => setJustificationId(event.target.value)}
              >
                <option value="">Selecione</option>
                {justifications?.trip.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.description}
                  </option>
                ))}
              </select>
            </label>
          )}
          {onCommand && !justifications?.trip.length && (
            <p role="alert">
              Não há justificativas operacionais ativas. Cadastre uma
              justificativa antes de rejeitar a viagem.
            </p>
          )}
          {actionError && <p role="alert">{actionError}</p>}
          <button
            className="stay-danger"
            disabled={
              !reason.trim() ||
              !access.rejectValidation ||
              busy ||
              Boolean(onCommand && !justificationId)
            }
            onClick={async () => {
              const success = await run(
                {
                  type: 'rejectTrip',
                  id: rejectTrip,
                  reason,
                  justificationId: Number(justificationId),
                },
                (current) => validateTrip(current, rejectTrip, false, reason),
                'Rejeição operacional registrada.'
              );
              if (success) setRejectTrip(null);
            }}
          >
            Confirmar rejeição
          </button>
        </Modal>
      )}
      {target && access.viewInvoices && (
        <Modal
          title={target.id}
          description={`${target.carrier} · ${target.tripIds.length} viagens · ${money(invoiceTotal(state, target))}`}
          onClose={() => setInvoiceId(null)}
        >
          <div className="stay-detail-summary">
            <span className={`stay-pill ${target.status}`}>
              {INVOICE_LABELS[target.status]}
            </span>
            <button onClick={() => exportInvoice(state, target)}>
              <ArrowDownToLine size={15} /> Exportar fatura
            </button>
          </div>
          <TripTable
            trips={state.trips.filter((trip) =>
              target.tripIds.includes(trip.id)
            )}
            onSelect={onSelect}
          />
          {target.status === 'pending' &&
            (access.approveInvoice || access.rejectInvoice) && (
              <section className="stay-review">
                <h3>Decisão Corteva</h3>
                {onCommand && access.rejectInvoice && (
                  <label className="stay-field">
                    Justificativa
                    <select
                      value={justificationId}
                      onChange={(event) =>
                        setJustificationId(event.target.value)
                      }
                    >
                      <option value="">Selecione</option>
                      {justifications?.billing.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.description}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {access.rejectInvoice && (
                  <label className="stay-field">
                    Motivo da reprovação
                    <textarea
                      placeholder="Obrigatório ao reprovar"
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                    />
                  </label>
                )}
                <div className="stay-actions">
                  {access.approveInvoice && (
                    <button
                      className="stay-primary"
                      disabled={busy}
                      onClick={() =>
                        run(
                          { type: 'approveInvoice', id: target.id },
                          (current) => decideInvoice(current, target.id, true),
                          'Fatura aprovada.'
                        )
                      }
                    >
                      <ShieldCheck size={16} /> Aprovar fatura
                    </button>
                  )}
                  {access.rejectInvoice && (
                    <button
                      className="stay-danger"
                      disabled={
                        !reason.trim() ||
                        busy ||
                        Boolean(onCommand && !justificationId)
                      }
                      onClick={() =>
                        run(
                          {
                            type: 'rejectInvoice',
                            id: target.id,
                            reason,
                            justificationId: Number(justificationId),
                          },
                          (current) =>
                            decideInvoice(current, target.id, false, reason),
                          'Fatura reprovada com justificativa.'
                        )
                      }
                    >
                      Reprovar fatura
                    </button>
                  )}
                </div>
              </section>
            )}
          {target.status === 'rejected' && (
            <div className="stay-review">
              <h3>Fatura reprovada</h3>
              <p>{target.reason}</p>
              {access.createInvoice && (
                <button
                  disabled={busy}
                  onClick={() =>
                    run(
                      { type: 'resubmitInvoice', id: target.id },
                      (current) => resubmitInvoice(current, target.id),
                      'Fatura reapresentada para aprovação Corteva.'
                    )
                  }
                >
                  Reapresentar fatura
                </button>
              )}
            </div>
          )}
          {target.status === 'approved' && (
            <>
              <section className="stay-protocol">
                <span>APROVAÇÃO CORTEVA</span>
                <strong>Fatura aprovada</strong>
                {delivery && (
                  <p>
                    <Check size={16} /> {delivery.status} em{' '}
                    {dateTime(delivery.at)}.
                  </p>
                )}
              </section>
              <section className="stay-review">
                <h3>
                  <Mail size={17} /> Encaminhamento à Deloitte
                </h3>
                <p>{RECIPIENTS.join(' · ')}</p>
                <details className="stay-email-preview">
                  <summary>Prévia do e-mail</summary>
                  <div className="stay-email">
                    <p>
                      <b>Para:</b> {RECIPIENTS.join('; ')}
                    </p>
                    <p>
                      <b>Assunto:</b> Corteva · aprovação de estadia ·{' '}
                      {target.id}
                    </p>
                    <hr />
                    <p>Prezados,</p>
                    <p>
                      A fatura {target.id}, da transportadora {target.carrier},
                      foi aprovada no valor de{' '}
                      <b>{money(invoiceTotal(state, target))}</b>.
                    </p>
                    <p>Viagens: {target.tripIds.join(', ')}.</p>
                    <p>
                      {delivery?.status === 'Enviado'
                        ? 'Mensagem encaminhada ao financeiro.'
                        : delivery?.status === 'Capturado no ambiente local'
                          ? 'Mensagem capturada no ambiente local, sem envio externo.'
                          : 'Prévia de mensagem. E-mail ainda não enviado.'}
                    </p>
                  </div>
                </details>
                {access.dispatch && (
                  <button
                    className="stay-primary"
                    disabled={Boolean(delivery) || busy}
                    onClick={() =>
                      run(
                        { type: 'dispatch', id: target.id },
                        (current) => deliverInvoice(current, target.id),
                        'Encaminhamento financeiro registrado.'
                      )
                    }
                  >
                    <Mail size={15} />{' '}
                    {delivery
                      ? onCommand
                        ? delivery.status
                        : 'Encaminhamento registrado'
                      : onCommand
                        ? 'Encaminhar ao financeiro'
                        : 'Registrar encaminhamento'}
                  </button>
                )}
              </section>
            </>
          )}
          <h3>
            <FileText size={17} /> Histórico da fatura
          </h3>
          {actionError && <p role="alert">{actionError}</p>}
          <ol className="stay-audit">
            {target.history.map((event, index) => (
              <li key={index}>
                <span>
                  {dateTime(event.at)} · {event.actor}
                </span>
                <p>{event.message}</p>
              </li>
            ))}
          </ol>
        </Modal>
      )}
    </>
  );
}
